import { TFooterLink, TFooterSection, useFooterLinks } from '@components/footer/footerLinks'
import { Box, Collapse, ExpandLessIcon, ExpandMoreIcon, Grid2, Typography, tss } from '@ds/components'
import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { baseUrl } from '@utils/assets'
import { useCurrentUser } from '@hooks/useCurrentUser'
import EnhancedLink from '@components/EnhancedLink'

const useStyles = tss.create(({ theme }) => ({
    root: {
        marginTop: theme.spacing(8),
    },
    mainSection: {
        backgroundColor: '#1e70a1',
    },
    desktopGrid: {
        maxWidth: 1020,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: theme.spacing(4),

        '@media (max-width: 769px)': {
            display: 'none',
        },
    },
    linkGroup: {
        textAlign: 'center',
        marginTop: theme.spacing(4),
    },
    linkGroup1: {
        marginTop: theme.spacing(2),
    },
    linkGroup2: {
        paddingBottom: theme.spacing(2),
    },

    footerIcon: {
        width: '43px',
        height: '43px',
        margin: '0 4px',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '295px',
        display: 'inline-block',
        verticalAlign: 'middle',
    },
    footerIconLink: {
        backgroundImage: `url(${baseUrl}images/footer-icons@2x.png)`,
    },
    footerLegalContentItem: {
        margin: '0 8px',
        fontSize: '11px',
        color: '#fff',
    },
    footerLink: {
        display: 'block',
        color: '#fff',
        padding: '0 16px',
        fontSize: '12px',
        marginBottom: 8,
        lineHeight: 'normal',

        '&:hover': {
            textDecoration: 'underline',
        },

        [theme.breakpoints.up('md')]: {
            marginBottom: '8px',
            padding: 0,
            lineHeight: 'normal',
        },
    },

    footerDecoration: {
        height: '64px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom',
        backgroundSize: 'auto 100%',

        [theme.breakpoints.up('md')]: {
            height: '100px',
            maxWidth: '1020px',
            margin: '0 auto',

            backgroundSize: '99% auto',
            backgroundPosition: 'bottom -1px center',
        },
    },

    footerExtraContent: {
        padding: '8px 0',
        textAlign: 'center',
        backgroundColor: '#ff9e1b',
    },

    footerHeading: {
        fontFamily: 'Bryant, sans-serif',
        fontSize: '16px',
        fontWeight: '600',
        textTransform: 'uppercase',
        lineHeight: 1,
        color: 'white',
        margin: '13.28px 0',
    },

    footerCollapsibleHeading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '40px',
        margin: 0,
        padding: '0 16px',
    },

    footerLinkSeeAll: {
        whiteSpace: 'nowrap',
        color: '#0e3247',
        textDecoration: 'underline',
    },

    youtube: {
        backgroundPosition: '-230px -138px',
        '&:hover': {
            backgroundPosition: '-248px -67px',
        },
    },

    instagram: {
        backgroundPosition: '-92px -138px',

        '&:hover': {
            backgroundPosition: '-46px -138px',
        },
    },

    linkedin: {
        backgroundPosition: '-184px -138px',
        '&:hover': {
            backgroundPosition: '-138px -138px',
        },
    },

    facebook: {
        backgroundPosition: '-202px -67px',
        '&:hover': {
            backgroundPosition: '0 -138px',
        },
    },

    googleplay: {
        width: '146px',
        backgroundPosition: '-149px 0',
        '&:hover': {
            width: '146px',
            backgroundPosition: '0 0',
        },
    },

    applestore: {
        width: '146px',
        backgroundPosition: '0 -92px',
        '&:hover': {
            width: '146px',
            backgroundPosition: '0 -46px',
        },
    },

    hide: {
        display: 'none',
        visibility: 'hidden',
    },

    showOnMobile: {
        display: 'block',

        '@media (min-width: 1024px)': {
            display: 'none',
        },
    },
}))

type CollapsibleSectionProps = {
    heading: string
    children: ReactNode
    defaultExpanded?: boolean
}

const expandLess = <ExpandLessIcon color="primary.contrastText" />
const expandMore = <ExpandMoreIcon color="primary.contrastText" />

function CollapsibleSection({ heading, children, defaultExpanded = false }: CollapsibleSectionProps): ReactElement {
    const [isExpanded, setExpanded] = useState(defaultExpanded)
    const onClick = useCallback(() => setExpanded(!isExpanded), [isExpanded])
    const { classes, cx } = useStyles()

    return (
        <>
            <Typography
                variant="h2"
                className={cx(classes.footerCollapsibleHeading, classes.footerHeading)}
                onClick={onClick}
                role="button"
                aria-expanded={isExpanded}
            >
                {heading}
                {isExpanded ? expandLess : expandMore}
            </Typography>
            <Collapse in={isExpanded} timeout="auto">
                <>{children}</>
            </Collapse>
        </>
    )
}

function FooterIcon({ route, icon, title }: { route: string; icon: string; title: string }): ReactElement {
    const { classes, cx } = useStyles()

    return (
        <EnhancedLink
            className={cx(classes.footerIconLink, classes.footerIcon, classes[icon as keyof typeof classes])}
            to={route}
            title={title}
        />
    )
}

type AnchorProps = { className?: string; rel?: string; target?: string }

function FooterLink({ text, route, openInNewTab = false }: TFooterLink): ReactElement | null {
    const { classes } = useStyles()
    const anchorProps: AnchorProps = useMemo(() => {
        const props: AnchorProps = { className: classes.footerLink }

        if (openInNewTab) {
            props.rel = 'noopener noreferrer'
            props.target = '_blank'
        }

        return props
    }, [openInNewTab])

    return (
        <EnhancedLink to={route} {...anchorProps}>
            {text}
        </EnhancedLink>
    )
}

function FooterLinksList({ links }: { links: TFooterLink[] }): ReactElement {
    return (
        <>
            {links
                .filter(({ include = true }) => include)
                .map(({ text, route, openInNewTab = false }: TFooterLink) => (
                    <FooterLink key={text} text={text} route={route} openInNewTab={openInNewTab} />
                ))}
        </>
    )
}

function StaticLinks() {
    const { classes, cx } = useStyles()
    const { data: currentUser } = useCurrentUser()

    return (
        <>
            <Box className={cx(classes.linkGroup, classes.linkGroup1)}>
                <Typography variant="h2" className={classes.footerHeading}>
                    Follow us
                </Typography>
                <FooterIcon
                    icon="facebook"
                    title="InterNations Facebook"
                    route="https://www.facebook.com/InterNations.org"
                />
                <FooterIcon
                    icon="linkedin"
                    title="InterNations Linkedin"
                    route="https://www.linkedin.com/company/internations"
                />
                <FooterIcon
                    icon="instagram"
                    title="InterNations Instagram"
                    route="https://www.instagram.com/internationsorg"
                />
                <FooterIcon
                    icon="youtube"
                    title="InterNations Youtube"
                    route="https://www.youtube.com/user/InterNationsVideos"
                />
            </Box>
            <Box className={cx(classes.linkGroup)}>
                <Typography variant="h2" className={classes.footerHeading}>
                    Download our Apps
                </Typography>
                <FooterIcon
                    icon="applestore"
                    title="InterNations Apple Store"
                    route="https://apps.apple.com/app/internations/id1059342646?mt=8"
                />
                <FooterIcon
                    icon="googleplay"
                    title="InterNations Google Play"
                    route="https://play.google.com/store/apps/details?id=org.internations"
                />
            </Box>
            <Box className={cx(classes.linkGroup, classes.linkGroup2)}>
                <Box className={classes.footerLegalContentItem}>
                    {`Made with ❤ in `}
                    <span title="Germany">Munich</span>
                    {` & `}
                    <span title="Lithuania">Vilnius</span>
                </Box>
                <EnhancedLink
                    className={classes.footerLegalContentItem}
                    to={`https://www.internations.org/about/imprint${currentUser ? `?ref=fo_im` : ''}`}
                >
                    Imprint
                </EnhancedLink>
                <EnhancedLink
                    className={classes.footerLegalContentItem}
                    to={`https://www.internations.org/terms-and-conditions${currentUser ? `?ref=fo_tc` : ''}`}
                >
                    Terms & Conditions
                </EnhancedLink>
                <EnhancedLink
                    className={classes.footerLegalContentItem}
                    to={`https://www.internations.org/privacy-policy${currentUser ? `?ref=fo_pp` : ''}`}
                >
                    Privacy Policy
                </EnhancedLink>
            </Box>
        </>
    )
}

function MainSection(): ReactElement {
    const { classes } = useStyles()
    const footerLinks = useFooterLinks()

    return (
        <Box className={classes.mainSection}>
            <Box py={2} className={classes.showOnMobile}>
                {footerLinks.map(
                    ({ heading, links, defaultExpanded }: TFooterSection) =>
                        heading && (
                            <CollapsibleSection heading={heading} key={heading} defaultExpanded={defaultExpanded}>
                                <FooterLinksList links={links ? links : []} />
                            </CollapsibleSection>
                        ),
                )}
            </Box>
            <Grid2 container spacing={2} className={classes.desktopGrid}>
                {footerLinks.map(({ heading, links }: TFooterSection) => (
                    <Grid2 size={{ xs: 6, sm: 3 }} key={heading}>
                        <Typography className={classes.footerHeading} variant="h2">
                            {heading}
                        </Typography>
                        <FooterLinksList links={links ? links : []} />
                    </Grid2>
                ))}
            </Grid2>

            <StaticLinks />
        </Box>
    )
}

function ExtraContent() {
    const { classes } = useStyles()
    return (
        <Box className={classes.footerExtraContent}>
            <span>{'InterNations is present in 420 cities worldwide. '}</span>
            <EnhancedLink
                className={classes.footerLinkSeeAll}
                to="https://www.internations.org/all-internations-communities"
            >
                See all InterNations Communities
            </EnhancedLink>
        </Box>
    )
}

export default function Footer(): ReactElement {
    const { classes } = useStyles()
    const { data: currentUser } = useCurrentUser()
    const disableNavigation =
        currentUser && currentUser.isInDunning && !currentUser?.permissions.canPayDunningLater.granted

    return (
        <footer className={classes.root}>
            <Box
                id="footer__decoration"
                className={classes.footerDecoration}
                style={{ backgroundImage: `url(${baseUrl}images/skyline.svg)` }}
            />

            {disableNavigation ? null : <MainSection />}
            <ExtraContent />
        </footer>
    )
}
